// frontend/src/components/dashboard/pos/hooks/useResponsiveLayout.js
import { useState, useEffect } from 'react';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const useResponsiveLayout = () => {
  const _theme = useTheme();
  const [deviceInfo, setDeviceInfo] = useState({
    type: 'desktop',
    orientation: 'landscape',
    screenSize: 'large'
  });

  // ENHANCED: Modern iPhone detection with correct viewport sizes
  // iPhone Mini (12 Mini, 13 Mini): 375px
  const isIPhoneMini = useMediaQuery('(max-width: 375px)');
  
  // iPhone Standard (12, 13, 14): 390px
  const isIPhoneStandard = useMediaQuery('(min-width: 376px) and (max-width: 393px)');
  
  // iPhone Plus/Pro Max (12 Pro Max, 13 Pro Max, 14 Plus, 15 Pro Max): 428-430px
  const isIPhonePlus = useMediaQuery('(min-width: 394px) and (max-width: 430px)');
  
  // Any iPhone in portrait (combining all iPhone sizes)
  const isIPhonePortrait = useMediaQuery('(max-width: 430px) and (orientation: portrait)');
  
  // Small phones in portrait - MOBILE CART MODE
  const isMobileCartMode = useMediaQuery('(max-width: 430px) and (orientation: portrait)');
  
  // Small tablets (iPad Mini): 668px - 834px
  const isSmallTablet = useMediaQuery('(min-width: 431px) and (max-width: 834px)');
  
  // Medium tablets (iPad Air): 835px - 1080px
  const isMediumTablet = useMediaQuery('(min-width: 835px) and (max-width: 1080px)');
  
  // Large tablets (iPad Pro): 1081px - 1366px
  const isLargeTablet = useMediaQuery('(min-width: 1081px) and (max-width: 1366px)');
  
  // Desktop: 1367px+
  const isDesktop = useMediaQuery('(min-width: 1367px)');
  
  // Orientation detection
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const isLandscape = useMediaQuery('(orientation: landscape)');

  // Enhanced device detection with mobile-first approach
  useEffect(() => {
    const detectDevice = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /ipad|iphone|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      let deviceType = 'desktop';
      let screenSize = 'large';
      let showMobileCart = false;
      let showDesktopCart = true;
      
      const width = window.innerWidth;
      const height = window.innerHeight;
      const pixelRatio = window.devicePixelRatio || 1;
      
      // MOBILE-FIRST APPROACH: Detect actual phones properly
      if (isMobileCartMode && isPortrait) {
        // TRUE MOBILE: iPhones in portrait mode
        deviceType = 'mobile';
        screenSize = 'small';
        showMobileCart = true;
        showDesktopCart = false;
        
        // Detect specific iPhone models for better optimization
        if (isIPhoneMini) {
          screenSize = 'mini'; // iPhone 12/13 Mini
        } else if (isIPhoneStandard) {
          screenSize = 'standard'; // iPhone 12/13/14
        } else if (isIPhonePlus) {
          screenSize = 'plus'; // iPhone Pro Max models
        }
      } else if (isSmallTablet) {
        // SMALL TABLET: iPad Mini - desktop layout, compact
        deviceType = 'tablet';
        screenSize = 'small';
        showMobileCart = false;
        showDesktopCart = true;
      } else if (isMediumTablet) {
        // MEDIUM TABLET: iPad Air - desktop layout, medium
        deviceType = 'tablet';
        screenSize = 'medium';
        showMobileCart = false;
        showDesktopCart = true;
      } else if (isLargeTablet) {
        // LARGE TABLET: iPad Pro - desktop layout, large
        deviceType = 'tablet';
        screenSize = 'large';
        showMobileCart = false;
        showDesktopCart = true;
      } else {
        // DESKTOP: Everything else
        deviceType = 'desktop';
        screenSize = 'large';
        showMobileCart = false;
        showDesktopCart = true;
      }

      // Special case: Phones in landscape get compact desktop layout
      if (width <= 926 && isLandscape && height >= 300) {
        deviceType = 'tablet';
        screenSize = 'small';
        showMobileCart = false;
        showDesktopCart = true;
      }

      setDeviceInfo({
        type: deviceType,
        orientation: isPortrait ? 'portrait' : 'landscape',
        screenSize,
        showMobileCart,
        showDesktopCart,
        isIOS,
        isAndroid,
        isTouch,
        pixelRatio,
        viewport: { width, height },
        // Enhanced device detection flags
        isMobileCartMode,
        isIPhoneMini,
        isIPhoneStandard,
        isIPhonePlus,
        isIPhonePortrait,
        isSmallTablet,
        isMediumTablet,
        isLargeTablet,
        isDesktop,
        // Safe area detection for iPhone notches
        hasSafeArea: isIOS && (isIPhonePortrait || (isLandscape && width <= 926)),
        // Touch optimization (consolidated)
        isTouchOptimized: isTouch && (deviceType === 'mobile' || deviceType === 'tablet')
      });
    };

    detectDevice();
    
    // Enhanced event listeners with debouncing
    let resizeTimeout;
    const debouncedDetect = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(detectDevice, 100);
    };
    
    window.addEventListener('resize', debouncedDetect);
    window.addEventListener('orientationchange', () => {
      setTimeout(detectDevice, 200); // Longer delay for orientation change
    });
    
    // Modern orientation API
    if (screen.orientation) {
      screen.orientation.addEventListener('change', detectDevice);
    }

    return () => {
      window.removeEventListener('resize', debouncedDetect);
      window.removeEventListener('orientationchange', detectDevice);
      if (screen.orientation) {
        screen.orientation.removeEventListener('change', detectDevice);
      }
      clearTimeout(resizeTimeout);
    };
  }, [
    isMobileCartMode, isIPhoneMini, isIPhoneStandard, isIPhonePlus,
    isSmallTablet, isMediumTablet, isLargeTablet, isDesktop,
    isPortrait, isLandscape, isIPhonePortrait
  ]);

  // MOBILE-FIRST layout configurations
  const getLayoutConfig = () => {
    const { type, screenSize, showMobileCart, showDesktopCart } = deviceInfo;
    
    const configs = {
      mobile: {
        mini: { // iPhone 12/13 Mini
          cartWidth: '100%',
          categoriesWidth: 0,
          categoriesLayout: 'none',
          productsColumns: { xs: 2 },
          cartHeight: '100vh',
          showMobileCart: true,
          showDesktopCart: false,
          paymentButtonGrid: 'repeat(2, 1fr)',
          fontSize: { small: '0.8rem', medium: '0.9rem', large: '1rem' },
          spacing: 1.2,
          touchTarget: 48, // iOS HIG minimum
          cartButtonSize: { width: 180, height: 52 }
        },
        standard: { // iPhone 12/13/14
          cartWidth: '100%',
          categoriesWidth: 0,
          categoriesLayout: 'none',
          productsColumns: { xs: 2 },
          cartHeight: '100vh',
          showMobileCart: true,
          showDesktopCart: false,
          paymentButtonGrid: 'repeat(2, 1fr)',
          fontSize: { small: '0.85rem', medium: '0.95rem', large: '1.05rem' },
          spacing: 1.4,
          touchTarget: 52,
          cartButtonSize: { width: 200, height: 56 }
        },
        plus: { // iPhone Pro Max models
          cartWidth: '100%',
          categoriesWidth: 0,
          categoriesLayout: 'none',
          productsColumns: { xs: 2 },
          cartHeight: '100vh',
          showMobileCart: true,
          showDesktopCart: false,
          paymentButtonGrid: 'repeat(2, 1fr)',
          fontSize: { small: '0.9rem', medium: '1rem', large: '1.1rem' },
          spacing: 1.6,
          touchTarget: 56,
          cartButtonSize: { width: 220, height: 60 }
        }
      },
      tablet: {
        small: { // iPad Mini
          cartWidth: 280,
          categoriesWidth: 80,
          categoriesLayout: 'sidebar',
          productsColumns: { xs: 3, sm: 4 },
          cartHeight: '65vh',
          showMobileCart: false,
          showDesktopCart: true,
          paymentButtonGrid: 'repeat(3, 1fr)',
          fontSize: { small: '0.7rem', medium: '0.8rem', large: '0.9rem' },
          spacing: 0.75,
          touchTarget: 44,
          cartButtonSize: null
        },
        medium: { // iPad Air
          cartWidth: 320,
          categoriesWidth: 90,
          categoriesLayout: 'sidebar',
          productsColumns: { xs: 3, sm: 4, md: 5 },
          cartHeight: '68vh',
          showMobileCart: false,
          showDesktopCart: true,
          paymentButtonGrid: 'repeat(3, 1fr)',
          fontSize: { small: '0.75rem', medium: '0.85rem', large: '0.95rem' },
          spacing: 1,
          touchTarget: 46,
          cartButtonSize: null
        },
        large: { // iPad Pro
          cartWidth: 360,
          categoriesWidth: 100,
          categoriesLayout: 'sidebar',
          productsColumns: { xs: 3, sm: 4, md: 5, lg: 6 },
          cartHeight: '70vh',
          showMobileCart: false,
          showDesktopCart: true,
          paymentButtonGrid: 'repeat(3, 1fr)',
          fontSize: { small: '0.8rem', medium: '0.9rem', large: '1rem' },
          spacing: 1.25,
          touchTarget: 48,
          cartButtonSize: null
        }
      },
      desktop: {
        cartWidth: 380,
        categoriesWidth: 120,
        categoriesLayout: 'sidebar',
        productsColumns: { xs: 6, sm: 4, md: 3, lg: 2.4, xl: 2 },
        cartHeight: '70vh',
        showMobileCart: false,
        showDesktopCart: true,
        paymentButtonGrid: 'repeat(3, 1fr)',
        fontSize: { small: '0.9rem', medium: '1rem', large: '1.1rem' },
        spacing: 1.5,
        touchTarget: 40,
        cartButtonSize: null
      }
    };

    let config;
    if (type === 'mobile') {
      config = configs.mobile[screenSize] || configs.mobile.standard;
    } else if (type === 'tablet') {
      config = configs.tablet[screenSize] || configs.tablet.medium;
    } else {
      config = configs.desktop;
    }

    // Override with actual device detection results
    return {
      ...config,
      showMobileCart,
      showDesktopCart
    };
  };

  return {
    deviceInfo,
    layoutConfig: getLayoutConfig(),
    getLayoutConfig,
    // CORRECTED: Use device detection results
    isMobile: deviceInfo.showMobileCart,
    isTablet: deviceInfo.type === 'tablet',
    isDesktop: deviceInfo.type === 'desktop',
    isSmallTablet: deviceInfo.isSmallTablet,
    isMediumTablet: deviceInfo.isMediumTablet,
    isLargeTablet: deviceInfo.isLargeTablet,
    isPortrait,
    isLandscape,
    // Cart visibility helpers - ENHANCED
    shouldShowMobileCart: deviceInfo.showMobileCart,
    shouldShowDesktopCart: deviceInfo.showDesktopCart,
    // Enhanced device detection
    isIPhone: deviceInfo.isIOS && deviceInfo.showMobileCart,
    isIPhoneMini: deviceInfo.isIPhoneMini,
    isIPhoneStandard: deviceInfo.isIPhoneStandard,
    isIPhonePlus: deviceInfo.isIPhonePlus,
    hasSafeArea: deviceInfo.hasSafeArea,
    // Utility functions
    shouldShowSidebar: () => deviceInfo.showDesktopCart,
    shouldUseTabs: () => deviceInfo.showMobileCart,
    getOptimalFontSize: () => {
      const config = getLayoutConfig();
      return config.fontSize;
    },
    getCartWidth: () => {
      const config = getLayoutConfig();
      return config.cartWidth;
    },
    getCategoriesWidth: () => {
      const config = getLayoutConfig();
      return config.categoriesWidth;
    },
    getProductColumns: () => {
      const config = getLayoutConfig();
      return config.productsColumns;
    },
    getSpacing: () => {
      const config = getLayoutConfig();
      return config.spacing;
    },
    getTouchTarget: () => {
      const config = getLayoutConfig();
      return config.touchTarget;
    },
    getCartButtonSize: () => {
      const config = getLayoutConfig();
      return config.cartButtonSize;
    },
    shouldOptimizeForTouch: () => deviceInfo.isTouchOptimized,
    getDeviceBrand: () => deviceInfo.isIOS ? 'Apple' : (deviceInfo.isAndroid ? 'Android' : 'Desktop')
  };
};

export default useResponsiveLayout;